import React, {useMemo} from "react";
import {Button} from "primereact/button";
import {buildSearchNeeds, digits, isVideo, knowledgeHealth, videoOfArticle} from "./help-cms";
import {Card, TabHeader} from "./ui";

const FLOW = [
    ["۱", "محتوا", "عنوان، موضوع و برچسب"],
    ["۲", "ارتباط", "مقاله ↔ ویدئو"],
    ["۳", "محل نمایش", "عمومی، ویجت یا AI"],
    ["۴", "انتشار", "قابل جست‌وجو برای کاربر"],
];

function StatButton({value, label, hint, attention, onClick}) {
    return (
        <div className="col-12 md:col-6 xl:col-3">
            <button type="button" onClick={onClick}
                    className={`w-full text-right surface-card border-1 border-200 border-round-xl p-3 cursor-pointer hover:border-primary ${attention ? "border-top-3 border-top-orange-500" : ""}`}>
                <b className="block text-3xl">{value}</b>
                <span className="text-sm text-500">{label}</span>
                <small className="block mt-2 text-primary">{hint} ←</small>
            </button>
        </div>
    );
}

function Task({icon, title, description, action, onAction}) {
    return (
        <div className="flex align-items-center gap-3 py-3 border-bottom-1 border-200">
            <span className="border-circle surface-100 flex align-items-center justify-content-center" style={{width: 40, height: 40}}>{icon}</span>
            <div className="flex-1">
                <b className="text-sm">{title}</b>
                <p className="m-0 mt-1 text-xs text-500">{description}</p>
            </div>
            <Button className="p-button-outlined p-button-sm text-xs" label={action} onClick={onAction}/>
        </div>
    );
}

export default function OverviewTab({data, openTab, onNew}) {
    const {contents, logs} = data;
    const published = contents.filter((item) => item.contentStatus === "published");
    const review = contents.filter((item) => item.contentStatus === "review");
    const unpaired = published.filter((item) => !isVideo(item) && !videoOfArticle(contents, item));
    const needs = useMemo(() => buildSearchNeeds(logs, contents), [logs, contents]);
    const health = useMemo(() => knowledgeHealth(contents), [contents]);
    const topNeed = needs[0];

    return (
        <div className="flex flex-column gap-3">
            <TabHeader kicker="نمای کلی" title="امروز چه کاری لازم است؟" description="هر عدد به یک اقدام مشخص وصل است." onNew={onNew}/>

            <div className="grid m-0">
                <StatButton value={digits(published.length)} label="محتوای منتشرشده" hint="مشاهده و ویرایش" onClick={() => openTab("contents")}/>
                <StatButton attention value={digits(review.length)} label="نیازمند بازبینی" hint="بررسی تغییرات محصول" onClick={() => openTab("contents", {status: "review"})}/>
                <StatButton value={digits(unpaired.length)} label="بدون نسخه مکمل" hint="اتصال مقاله و ویدئو" onClick={() => openTab("relations")}/>
                <StatButton attention value={digits(needs.length)} label="جست‌وجوی بدون پاسخ" hint="تبدیل به محتوا" onClick={() => openTab("search")}/>
            </div>

            <Card title="مسیر انتشار محتوا" description="هر تغییر در مدیریت، دقیقاً در این چهار گام اثر می‌گذارد.">
                <div className="grid m-0 align-items-stretch">
                    {FLOW.map(([number, title, description], index) => (
                        <div key={title} className="col-12 md:col-3">
                            <div className={`h-full border-1 border-round-lg p-3 flex flex-column gap-1 ${index === 3 ? "border-primary surface-100" : "border-200"}`}>
                                <span className="text-primary font-bold">{number}</span>
                                <b className="text-sm">{title}</b>
                                <small className="text-500">{description}</small>
                            </div>
                        </div>
                    ))}
                </div>
            </Card>

            <div className="grid m-0">
                <div className="col-12 lg:col-8">
                    <Card title="اقدام پیشنهادی" className="h-full">
                        {unpaired.length > 0 && (
                            <Task icon="⇄" title={`${digits(unpaired.length)} مقاله ویدئوی مکمل ندارند`}
                                  description="کاربر در این مقاله‌ها مجبور است جداگانه جست‌وجو کند."
                                  action="رفع مشکل" onAction={() => openTab("relations")}/>
                        )}
                        {topNeed && (
                            <Task icon="⌕" title={`«${topNeed.query}» ${digits(topNeed.count)} بار جست‌وجو شده`}
                                  description="هیچ محتوای دقیقی برای این نیاز وجود ندارد."
                                  action="بررسی" onAction={() => openTab("search")}/>
                        )}
                        {review.length > 0 && (
                            <Task icon="▤" title={`${digits(review.length)} محتوا منتظر بازبینی است`}
                                  description="پس از بازبینی، محتوا را منتشر کنید."
                                  action="مشاهده" onAction={() => openTab("contents", {status: "review"})}/>
                        )}
                        {!unpaired.length && !topNeed && !review.length && <p className="text-sm text-500 m-0">اقدام فوری وجود ندارد.</p>}
                    </Card>
                </div>
                <div className="col-12 lg:col-4">
                    <Card title="سلامت پایگاه دانش" className="h-full">
                        <b className="block text-4xl text-primary">{digits(health.score)}٪</b>
                        <p className="text-xs text-500">{health.weakest}</p>
                        <div className="surface-200 border-round overflow-hidden" style={{height: 8}}>
                            <div className="bg-primary h-full" style={{width: `${health.score}%`}}/>
                        </div>
                    </Card>
                </div>
            </div>
        </div>
    );
}
